<section class="surface" role="region" aria-labelledby="user-update-heading">
	<h2 id="user-update-heading" lvrPadding [padHorizontal]="32" [padVertical]="0">
		{{ 'user.update_user.title' | transloco }}
	</h2>

	@if (form) {
		<form class="form-container" [formGroup]="form" lvrAutoSave="user" [entityId]="draftId" [autoLoad]="false">
			<!-- Base User Information Section -->
			<h3 id="user-base-heading">{{ 'user.create_user.base_info' | transloco }}</h3>
			<section class="form-grid" aria-labelledby="user-base-heading" formGroupName="baseInfo">
				<!-- Title -->
				<lvr-field [label]="'base.field.title' | transloco">
					@let titleCtrl = form.get('baseInfo.title');
					<mat-form-field lvrInput [lvrError]="hasError(titleCtrl, 'pattern')">
						<input matInput formControlName="title" placeholder="Dr." [errorStateMatcher]="matcher" />
						@if (hasError(titleCtrl, 'pattern')) {
							<mat-error>{{ 'base.form.error.title_invalid' | transloco }}</mat-error>
						}
					</mat-form-field>
				</lvr-field>

				<!-- Placeholder (Spacer) -->
				<div></div>

				<!-- Last Name -->
				<lvr-field [label]="'base.field.last_name' | transloco | appendAsterisk">
					<p class="readonly">{{ user().lastName }}</p>
				</lvr-field>

				<!-- First Name -->
				<lvr-field [label]="'base.field.first_name' | transloco | appendAsterisk">
					@let firstNameCtrl = form.get('baseInfo.firstName');
					<mat-form-field lvrInput [lvrError]="hasError(firstNameCtrl, 'pattern', 'required')">
						<input matInput formControlName="firstName" placeholder="Max" [errorStateMatcher]="matcher" />
						@if (hasError(firstNameCtrl, 'pattern')) {
							<mat-error>{{ 'base.form.error.first_name_invalid' | transloco }}</mat-error>
						}
						@if (hasError(firstNameCtrl, 'required')) {
							<mat-error>{{ 'base.form.error.first_name_required' | transloco }}</mat-error>
						}
					</mat-form-field>
				</lvr-field>

				<!-- Gender -->
				<lvr-field [label]="'base.field.gender' | transloco | appendAsterisk">
					@let genderCtrl = form.get('baseInfo.genderId');
					<mat-form-field
						lvrInput
						[lvrError]="hasError(genderCtrl, 'required')"
						[attr.aria-label]="'base.field.gender' | transloco"
					>
						<mat-select
							formControlName="genderId"
							[placeholder]="'base.field.select_please' | transloco"
							aria-required="true"
							[errorStateMatcher]="matcher"
						>
							@for (option of availableGenders(); track option.id) {
								<mat-option
									[value]="option.id"
									matTooltipClass="info-tooltip"
									[matTooltip]="
										option.salutation === 'divers u.a.' ? ('client.create_client.gender_hint_divers' | transloco) : null
									"
								>
									{{ option.salutation }}
								</mat-option>
							}
						</mat-select>
						@if (hasError(genderCtrl, 'required')) {
							<mat-error>{{ 'base.form.error.gender_required' | transloco }}</mat-error>
						}
					</mat-form-field>
				</lvr-field>

				<!-- E-Mail -->
				<lvr-field [label]="'base.field.email' | transloco | appendAsterisk">
					<p class="readonly">{{ user().email }}</p>
				</lvr-field>

				<!-- Address -->
				<lvr-address-completion
					[showAsterisk]="false"
					class="address-completion"
					formControlName="address"
				></lvr-address-completion>

				<!-- Notes on accessability -->
				<lvr-field [label]="'base.field.notes_on_accessability' | transloco">
					<mat-form-field lvrInput>
						<input
							matInput
							formControlName="notesOnAccessibility"
							[placeholder]="'base.field.notes_on_accessability' | transloco"
						/>
					</mat-form-field>
				</lvr-field>

				<!-- Remark -->
				<lvr-field [label]="'base.field.remark' | transloco">
					@let remarkCtrl = form.get('baseInfo.remark');
					<mat-form-field lvrInput>
						<input
							matInput
							formControlName="remark"
							[placeholder]="'base.field.remark' | transloco"
							[errorStateMatcher]="matcher"
						/>
						@if (hasError(remarkCtrl, 'maxLength')) {
							<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: 200 } }}</mat-error>
						}
					</mat-form-field>
				</lvr-field>
			</section>

			<!-- Contact Point Field -->
			@for (item of contactPoints; track item; let index = $index) {
				<section class="form-grid">
					<h3 lvrPadding class="additional-communication-heading" [padVertical]="0" [padHorizontal]="8">
						<span>{{ 'base.field.contact_point.title' | transloco }}</span>
						<button matIconButton class="btn btn-tertiary" type="button" (click)="removeContactPoint(index)">
							<mat-icon class="material-symbols-outlined" aria-hidden="true">delete</mat-icon>
						</button>
					</h3>
					<lvr-contact-point
						[contactPointCategories]="contactPointCategories()"
						class="address-completion"
						[initValue]="contactPoints[$index]"
						(values)="updateValues($event, $index)"
					></lvr-contact-point>
				</section>
			}

			<section class="form-grid">
				<!-- Add additional button -->
				<lvr-button
					lvrPadding
					[padHorizontal]="32"
					[padVertical]="0"
					variant="primary"
					[label]="'base.field.contact_point.title' | transloco"
					[ariaLabel]="'base.field.contact_point.add_communication' | transloco"
					[type]="'button'"
					(clicked)="addContactPoint()"
					icon="add"
				></lvr-button>
			</section>

			<!-- Is Severely Disabled -->
			<section class="checkbox-group" lvrPadding [padHorizontal]="16" [formGroup]="form" formGroupName="baseInfo">
				<p class="label">{{ 'user.update_user.severe_disability_hint' | transloco }}</p>

				<lvr-field [label]="'user.update_user.severe_disability' | transloco">
					<br />
					<mat-checkbox
						formControlName="isSeverelyDisabled"
						[attr.aria-label]="'user.update_user.is_severely_disabled_true' | transloco"
					>
						{{ 'user.update_user.is_severely_disabled_true' | transloco }}
					</mat-checkbox>
				</lvr-field>
			</section>

			<p class="required-hint">{{ 'base.form.required' | transloco }}</p>

			<lvr-default-actions
				[disableSave]="form.invalid || loading()"
				(save)="onSave()"
				(cancelSave)="handleCancelEdit()"
				[disableCancelSave]="loading()"
				[showLoading]="loading()"
			>
				<lvr-restore-actions entityType="user" [entityId]="draftId"></lvr-restore-actions>
			</lvr-default-actions>
		</form>
	}
</section>
